import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useToast } from '../Toast';

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1024 / 1024).toFixed(1) + ' MB';
}

export default function BackupPage() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const toast = useToast();

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isAdmin) { nav('/'); return; }
    load();
  }, [isAdmin]); // eslint-disable-line

  async function load() {
    try {
      const r = await api.get('/backup');
      setBackups(r.data);
    } catch {
      toast('Błąd ładowania backupów', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    setCreating(true);
    try {
      await api.post('/backup');
      toast('Backup utworzony');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd tworzenia backupu', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDownload(filename) {
    try {
      const r = await api.get(`/backup/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast('Błąd pobierania backupu', 'error');
    }
  }

  async function handleDelete(filename) {
    if (!window.confirm(`Usunąć backup ${filename}?`)) return;
    try {
      await api.delete(`/backup/${filename}`);
      toast('Backup usunięty');
      load();
    } catch {
      toast('Błąd usuwania', 'error');
    }
  }

  if (!isAdmin) return null;

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => nav('/')}>← Wróć</button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--gray-900)' }}>Kopie zapasowe bazy danych</h1>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>Automatyczne kopie zapasowe</div>
            <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 4 }}>
              System automatycznie tworzy kopię każdej nocy o 02:00. Przechowuje ostatnie 30 kopii.
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? '⏳ Tworzenie...' : '+ Utwórz teraz'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, color: 'var(--gray-400)' }}>Ładowanie...</div>
      ) : backups.length === 0 ? (
        <div className="empty-state card"><p>Brak kopii zapasowych</p></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 13, color: 'var(--gray-500)', fontWeight: 600 }}>Plik</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 13, color: 'var(--gray-500)', fontWeight: 600 }}>Rozmiar</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 13, color: 'var(--gray-500)', fontWeight: 600 }}>Data</th>
                <th style={{ padding: '10px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.filename} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 13 }}>{b.filename}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-600)' }}>{formatBytes(b.size)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-600)' }}>
                    {b.created_at ? new Date(b.created_at).toLocaleString('pl-PL') : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(b.filename)}>⬇ Pobierz</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(b.filename)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
